<!-- src/components/PerformanceForm.vue -->
<template>
  <v-container fluid class="form-container">
    <v-card class="mx-auto mb-8 main-card" elevation="0" rounded="xl">
      <v-card-title class="text-h4 text-center py-6 title-gradient">
        Évaluation de niveau en haltérophilie
      </v-card-title>
      
      <v-card-text class="px-6 pt-6 pb-8">
        <v-form ref="form" v-model="isFormValid">
          <!-- Info/Intro section -->
          <v-sheet color="primary" rounded="lg" class="mb-6 pa-4" elevation="0">
            <div class="d-flex align-center">
              <v-icon icon="mdi-information-outline" class="mr-3" color="background"></v-icon>
              <span>Entrez vos informations et performances pour évaluer votre niveau selon les standards officiels</span>
            </div>
          </v-sheet>
          
          <!-- Profil -->
          <div class="form-section mb-6">
            <h3 class="text-h6 mb-4 d-flex align-center">
              <v-icon icon="mdi-account" class="mr-2" color="primary"></v-icon>
              Votre profil
            </h3>
            
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model.number="age"
                  label="Âge"
                  type="number"
                  min="13"
                  max="90"
                  required
                  variant="outlined"
                  prepend-inner-icon="mdi-calendar-account"
                  bg-color="surface"
                  class="custom-field"
                  density="comfortable"
                >
                  <template v-slot:append>
                    <v-chip
                      size="small"
                      color="purple-lighten-3"
                      variant="tonal"
                      v-if="ageCategoryText"
                    >
                      {{ ageCategoryText }}
                    </v-chip>
                  </template>
                </v-text-field>
              </v-col>
              
              <v-col cols="12" sm="6" md="4">
                <v-select
                  v-model="gender"
                  label="Sexe"
                  :items="[
                    { title: 'Homme', value: 'M' },
                    { title: 'Femme', value: 'F' }
                  ]"
                  item-title="title"
                  item-value="value"
                  required
                  variant="outlined"
                  prepend-inner-icon="mdi-gender-male-female"
                  bg-color="surface"
                  density="comfortable"
                  class="custom-field"
                ></v-select>
              </v-col>
              
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model.number="bodyWeight"
                  label="Poids de corps (kg)"
                  type="number"
                  min="30"
                  max="200"
                  step="0.1"
                  required
                  variant="outlined"
                  prepend-inner-icon="mdi-scale-bathroom"
                  bg-color="surface"
                  density="comfortable"
                  class="custom-field"
                >
                  <template v-slot:append>
                    <v-chip
                      size="small"
                      color="teal-lighten-3"
                      variant="tonal"
                      v-if="weightCategory"
                    >
                      {{ weightCategory }}
                    </v-chip>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </div>
          
          <!-- Performances -->
          <div class="form-section mb-6">
            <h3 class="text-h6 mb-4 d-flex align-center">
              <v-icon icon="mdi-dumbbell" class="mr-2" color="secondary"></v-icon>
              Vos performances
            </h3>
            
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="snatch"
                  label="Performance à l'arraché (kg)"
                  type="number"
                  min="0"
                  max="300"
                  step="0.5"
                  required
                  variant="outlined"
                  prepend-inner-icon="mdi-arm-flex"
                  bg-color="surface"
                  density="comfortable"
                  class="custom-field"
                  color="inflow"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="cleanAndJerk"
                  label="Performance à l'épaulé-jeté (kg)"
                  type="number"
                  min="0"
                  max="400"
                  step="0.5"
                  required
                  variant="outlined"
                  prepend-inner-icon="mdi-weight-lifter"
                  bg-color="surface"
                  density="comfortable"
                  class="custom-field"
                  color="outflow"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <!-- Total section avec l'affichage hexagonal -->
          <v-sheet 
            v-if="total > 0" 
            color="transparent" 
            class="mb-8 mt-4 d-flex justify-center"
          >
            <div class="total-hexagon">
              <div class="total-inner">
                <div class="total-title">TOTAL</div>
                <div class="total-value">{{ total }} kg</div>
                <div class="total-chips">
                  <v-chip
                    size="small"
                    :color="gender === 'M' ? 'blue-lighten-4' : 'pink-lighten-4'"
                    class="font-weight-bold mx-1"
                  >
                    {{ gender === 'M' ? 'H' : 'F' }}
                  </v-chip>
                  <v-chip
                    size="small"
                    color="purple-lighten-4"
                    class="mx-1 font-weight-bold"
                  >
                    {{ ageCategoryShort }}
                  </v-chip>
                  <v-chip
                    size="small"
                    color="teal-lighten-4"
                    class="mx-1 font-weight-bold"
                    v-if="weightCategory"
                  >
                    {{ weightCategoryShort }}
                  </v-chip>
                </div>
              </div>
            </div>
          </v-sheet>
          
          <!-- Action buttons -->
          <div class="d-flex justify-center mt-6">
            <v-btn
              color="primary"
              size="large"
              min-width="200"
              @click="evaluateLevel"
              :disabled="!isFormValid"
              prepend-icon="mdi-check-circle"
              class="evaluate-button"
              rounded="pill"
              elevation="3"
            >
              Évaluer mon niveau
            </v-btn>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
    
    <result-display 
      v-if="showResults" 
      :result="result"
      :age="age"
      :gender="gender"
      :bodyWeight="bodyWeight"
      :snatch="snatch"
      :cleanAndJerk="cleanAndJerk"
      :total="total"
    />
    
    <div class="text-center mt-4 mb-8">
      <v-btn
        color="secondary"
        variant="outlined"
        @click="goBack"
        prepend-icon="mdi-arrow-left"
        rounded="pill"
      >
        Retour
      </v-btn>
    </div>
  </v-container>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { evaluatePerformance, getWeightCategory, getAgeCategory } from '../services/performance';
import ResultDisplay from './ResultDisplay.vue';

export default {
  name: 'PerformanceForm',
  components: {
    ResultDisplay
  },
  setup() {
    const router = useRouter();
    
    const age = ref(25);
    const gender = ref('M');
    const bodyWeight = ref(73);
    const snatch = ref(70);
    const cleanAndJerk = ref(90);
    const showResults = ref(false);
    const result = ref(null);
    const isFormValid = ref(true);
    
    // Calcul du total
    const total = computed(() => {
      return snatch.value + cleanAndJerk.value;
    });
    
    // Calcul de la catégorie d'âge
    const ageCategory = computed(() => {
      return getAgeCategory(age.value);
    });
    
    // Version courte de la catégorie d'âge pour les chips
    const ageCategoryShort = computed(() => {
      const cat = ageCategory.value;
      if (cat === 'Master') return 'M';
      if (cat === 'Senior') return 'S';
      return cat; // U15, U17, U20 sont déjà courts
    });
    
    // Texte de la catégorie d'âge
    const ageCategoryText = computed(() => {
      const catMap = {
        'U15': 'Moins de 15 ans',
        'U17': 'Moins de 17 ans',
        'U20': 'Moins de 20 ans',
        'Senior': 'Senior',
        'Master': 'Master'
      };
      return catMap[ageCategory.value] || ageCategory.value;
    });
    
    // Calcul de la catégorie de poids
    const weightCategory = computed(() => {
      if (!bodyWeight.value) return '';
      return getWeightCategory(gender.value, bodyWeight.value, ageCategory.value);
    });
    
    // Version courte de la catégorie de poids pour les chips
    const weightCategoryShort = computed(() => {
      if (!weightCategory.value) return '';
      // Retirer le " kg" et garder seulement le nombre
      return weightCategory.value.replace(' kg', '');
    });
    
    // Surveiller les changements dans les valeurs d'entrée pour réinitialiser les résultats
    watch([age, gender, bodyWeight, snatch, cleanAndJerk], () => {
      if (showResults.value) {
        showResults.value = false;
      }
    });
    
    const evaluateLevel = () => {
      const performanceResult = evaluatePerformance(
        age.value, 
        gender.value, 
        snatch.value, 
        cleanAndJerk.value,
        bodyWeight.value
      );
      
      result.value = performanceResult;
      showResults.value = true;
    };
    
    const goBack = () => {
      router.push('/');
    };
    
    return {
      age,
      gender,
      bodyWeight,
      snatch,
      cleanAndJerk,
      showResults,
      result,
      isFormValid,
      total,
      ageCategory,
      ageCategoryText,
      ageCategoryShort,
      weightCategory,
      weightCategoryShort,
      evaluateLevel,
      goBack
    };
  }
}
</script>

<style scoped>
.form-container {
  max-width: 1200px;
  margin: 0 auto;
}

.main-card {
  background: linear-gradient(to right bottom, #232526, #414345);
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.title-gradient {
  /* Supprimons le gradient qui peut causer des problèmes sur certains navigateurs mobiles */
  color: var(--v-primary-base); /* Utilisez simplement la couleur primaire */
  font-weight: bold;
}

/* Pour les appareils qui supportent les gradients */
@supports (background-clip: text) or (-webkit-background-clip: text) {
  .title-gradient {
    background: linear-gradient(to right, var(--v-primary-base), var(--v-secondary-base));
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
}

.form-section {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.custom-field :deep(.v-field__outline) {
  opacity: 0.7;
}

.custom-field :deep(.v-field--focused .v-field__outline) {
  opacity: 1;
}

.total-hexagon {
  position: relative;
  width: 200px;
  height: 200px;
  background-color: var(--v-net-change-base);
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  display: flex;
  justify-content: center;
  align-items: center;
  transform: translateY(0);
  transition: transform 0.3s ease;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
}

.total-hexagon:hover {
  transform: translateY(-5px);
}

.total-inner {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  background-color: #1e1e1e;
  width: 90%;
  height: 90%;
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
}

.total-title {
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 2px;
  margin-bottom: 10px;
  color: var(--v-net-change-base);
}

.total-value {
  font-size: 2.5rem;
  font-weight: bold;
  line-height: 1;
  margin-bottom: 10px;
}

.total-chips {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
}

.evaluate-button {
  transition: transform 0.3s, box-shadow 0.3s;
}

.evaluate-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

@media (max-width: 600px) {
  .total-hexagon {
    width: 150px;
    height: 150px;
  }
  
  .total-value {
    font-size: 1.8rem;
  }
}
/* Amélioration des styles mobile */
@media (max-width: 600px) {
  .form-container {
    padding: 0 !important;
  }
  
  .main-card {
    margin: 0 !important;
    border-radius: 0 !important; /* Supprime les coins arrondis sur mobile */
  }
  
  .v-card-title {
    font-size: 1.5rem !important;
    padding: 16px 8px !important;
  }
  
  .v-card-text {
    padding: 16px 12px !important;
  }
  
  .form-section {
    padding: 12px !important;
    margin-bottom: 16px !important;
  }
  
  .text-h6 {
    font-size: 1.1rem !important;
  }
  
  /* Ajuster l'affichage hexagonal */
  .total-hexagon {
    width: 140px;
    height: 140px;
  }
  
  .total-value {
    font-size: 1.5rem;
  }
  
  .total-title {
    font-size: 0.8rem;
  }
  
  .v-btn {
    width: 100%; /* Boutons pleine largeur sur mobile */
  }
}
/* Styles pour les très petits écrans */
@media (max-width: 490px) {
  /* Réduire encore plus le padding */
  .v-card-text {
    padding: 0 10px !important;
  }
  .text-h3 {
    font-size: 2rem !important;
  }
  /* Adapter les sections de formulaire */
  .form-section {
    padding: 8px !important;
  }
  
  /* Réduire la taille de l'hexagone */
  .total-hexagon {
    width: 120px;
    height: 120px;
  }
  
  .total-value {
    font-size: 1.2rem;
  }
  
  .total-title {
    font-size: 0.7rem;
    margin-bottom: 5px;
  }
  
  /* Adapter la taille des puces */
  .total-chips .v-chip {
    font-size: 0.6rem !important;
    height: 20px !important;
  }
  
  /* Réduire l'espace entre les champs */
  .v-text-field, .v-select {
    margin-bottom: 4px !important;
  }
  
  /* Afficher correctement les icônes dans les champs */
  .v-field__prepend-inner {
    padding-right: 4px !important;
  }
  
  /* Réduire la taille du texte des étiquettes */
  .v-label {
    font-size: 0.8rem !important;
  }
  
  /* Assurer que le message d'information est lisible */
  .v-sheet {
    padding: 8px !important;
    font-size: 0.8rem !important;
  }
  
  /* Ajuster la taille du bouton d'évaluation */
  .evaluate-button {
    min-width: 100% !important;
    margin: 0 !important;
  }
}
</style>